import React, { useState, useRef, useEffect } from 'react';
import { ChevronDown, ChevronUp, X, ExternalLink } from 'lucide-react';
import { openExternalLink } from '../utils/externalLinks';

const HEADER_H = 56;

const EmbeddedFormSheet = ({ open, title, url, onClose }) => {
  const [minimized, setMinimized] = useState(false);
  const [dragY, setDragY] = useState(0);
  const [dragging, setDragging] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const startRef = useRef(null);

  useEffect(() => {
    if (open) { setMinimized(false); setDragY(0); setLoaded(false); }
  }, [open]);

  useEffect(() => {
    if (open && !minimized) {
      document.body.style.overflow = 'hidden';
      return () => { document.body.style.overflow = ''; };
    }
  }, [open, minimized]);

  if (!open) return null;

  const onPointerDown = (e) => {
    startRef.current = { y: e.clientY, wasMin: minimized };
    setDragging(true);
    try { e.currentTarget.setPointerCapture(e.pointerId); } catch (_) { /* noop */ }
  };

  const onPointerMove = (e) => {
    if (!startRef.current) return;
    let dy = e.clientY - startRef.current.y;
    if (startRef.current.wasMin) dy = Math.min(0, dy);
    else dy = Math.max(0, dy);
    setDragY(dy);
  };

  const onPointerUp = () => {
    if (!startRef.current) { setDragging(false); return; }
    const { wasMin } = startRef.current;
    const dy = dragY;
    startRef.current = null;
    setDragging(false);
    setDragY(0);
    if (!wasMin && dy > 100) setMinimized(true);
    else if (wasMin && dy < -60) setMinimized(false);
    else if (wasMin && Math.abs(dy) < 8) setMinimized(false);
  };

  const handleClose = (e) => {
    e.stopPropagation();
    if (window.confirm('Fechar o formulário? O que não foi enviado será perdido.')) onClose();
  };

  const baseTransform = minimized
    ? `translateY(calc(100% - ${HEADER_H}px - env(safe-area-inset-bottom, 0px)))`
    : 'translateY(0px)';
  const transform = dragging
    ? (minimized
        ? `translateY(calc(100% - ${HEADER_H}px - env(safe-area-inset-bottom, 0px) + ${dragY}px))`
        : `translateY(${dragY}px)`)
    : baseTransform;

  return (
    <div className={`fixed inset-0 z-[99990] ${minimized ? 'pointer-events-none' : ''}`} data-testid="embedded-form-sheet">
      {!minimized && (
        <div
          className="absolute inset-0 bg-black/40 transition-opacity"
          onClick={() => setMinimized(true)}
          data-testid="form-sheet-backdrop"
        />
      )}
      <div
        className="absolute inset-x-0 bottom-0 flex flex-col bg-white rounded-t-2xl shadow-2xl pointer-events-auto"
        style={{
          height: 'calc(100dvh - 40px)',
          transform,
          transition: dragging ? 'none' : 'transform 0.3s cubic-bezier(0.32, 0.72, 0, 1)',
          willChange: 'transform',
        }}
      >
        <div
          className="flex items-center gap-2 px-3 shrink-0 select-none cursor-grab active:cursor-grabbing border-b bg-slate-50 rounded-t-2xl touch-none"
          style={{ height: HEADER_H }}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerCancel={onPointerUp}
          data-testid="form-sheet-header"
        >
          <div className="absolute left-1/2 -translate-x-1/2 top-1.5 w-10 h-1 rounded-full bg-slate-300" />
          <span className="text-lg">📋</span>
          <span className="font-semibold text-sm text-slate-800 flex-1 truncate">{title}</span>
          {minimized && (
            <span className="text-[10px] text-emerald-600 font-medium bg-emerald-50 border border-emerald-200 rounded-full px-2 py-0.5 mr-1">
              aberto
            </span>
          )}
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); openExternalLink(url); }}
            onPointerDown={(e) => e.stopPropagation()}
            className="p-2 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-200"
            title="Abrir no navegador"
            data-testid="form-sheet-external-btn"
          >
            <ExternalLink className="h-4 w-4" />
          </button>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); setMinimized(!minimized); }}
            onPointerDown={(e) => e.stopPropagation()}
            className="p-2 rounded-lg text-slate-500 hover:text-slate-700 hover:bg-slate-200"
            title={minimized ? 'Expandir' : 'Minimizar'}
            data-testid="form-sheet-minimize-btn"
          >
            {minimized ? <ChevronUp className="h-5 w-5" /> : <ChevronDown className="h-5 w-5" />}
          </button>
          <button
            type="button"
            onClick={handleClose}
            onPointerDown={(e) => e.stopPropagation()}
            className="p-2 rounded-lg text-slate-500 hover:text-red-600 hover:bg-red-50"
            title="Fechar"
            data-testid="form-sheet-close-btn"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
        <div className="flex-1 relative bg-slate-100">
          {!loaded && (
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 text-slate-500">
              <div className="w-8 h-8 border-4 border-slate-300 border-t-purple-600 rounded-full animate-spin" />
              <p className="text-sm">Carregando formulário...</p>
            </div>
          )}
          <iframe
            src={url}
            title={title}
            onLoad={() => setLoaded(true)}
            className="w-full h-full border-0"
            style={{ pointerEvents: dragging ? 'none' : 'auto' }}
            data-testid="form-sheet-iframe"
          />
        </div>
      </div>
    </div>
  );
};

export default EmbeddedFormSheet;
